import { MessageCircle, Settings, Menu, X } from "lucide-react";
import { Alerts } from "./Alerts";
import { Link, useLocation } from "react-router-dom";
import { UserMenu } from "./auth/UserMenu";
import { PlanBadge } from "./PlanBadge";
import { useAuth } from "context/AuthContext";
import { useStudents } from "context/StudentsContext";
import { useState } from "react";

type SidebarProps = {
  teacherId: number;
  isStudentListOpen?: boolean;
  onChatClick?: () => void;
};

export function Sidebar({
  teacherId,
  isStudentListOpen = false,
  onChatClick,
}: SidebarProps) {
  const location = useLocation();
  const { planLimit, isImpersonating } = useAuth();
  const { activeStudentsCount } = useStudents();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMain = location.pathname === "/";
  const isSettings = location.pathname === "/settings";

  const baseClass =
    "w-10 h-10 rounded-full flex items-center justify-center transition-colors";

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      {/* Mobile menu button - hidden when student list is open */}
      {!isStudentListOpen && (
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          onTouchEnd={(e) => {
            // Prevent default to avoid double-tap zoom and ensure click fires
            e.preventDefault();
            setIsMobileMenuOpen(!isMobileMenuOpen);
          }}
          className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg border border-gray-200"
        >
          {isMobileMenuOpen ? (
            <X className="w-5 h-5 text-gray-600" />
          ) : (
            <Menu className="w-5 h-5 text-gray-600" />
          )}
        </button>
      )}

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-[99998]"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-[99999] transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        } w-16 bg-white border-r border-gray-200 flex flex-col items-center py-4 space-y-6 ${
          isImpersonating ? "border-l-4 border-l-red-600" : ""
        }`}
      >
        <Link
          to="/"
          className={`${baseClass} ${
            isMain
              ? "bg-blue-500 text-white"
              : "bg-blue-100 hover:bg-blue-200 text-blue-600"
          }`}
          title="Chats"
          onClick={() => {
            closeMobileMenu();
            onChatClick?.();
          }}
        >
          <MessageCircle className="w-5 h-5" />
        </Link>

        <Alerts teacherId={teacherId} />

        <div className="relative">
          <Link
            to="/settings"
            className={`${baseClass} ${
              isSettings
                ? "bg-gray-700 text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-600"
            }`}
            title="Settings"
            onClick={closeMobileMenu}
          >
            <Settings className="w-5 h-5" />
          </Link>
          <div className="absolute -top-0.5 -right-0.5">
            <PlanBadge
              studentCount={activeStudentsCount}
              planLimit={planLimit}
            />
          </div>
        </div>

        <div className="flex-1" />

        <UserMenu />
      </div>
    </>
  );
}
